import {useApolloClient} from '@apollo/client';
import {TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {updateChatSession} from 'llm/chat-library/ChatDAO';
import Modal from 'ui-library/modals/Modal';

// Keep in-sync with benchling/llm/chat_library/domain/constants.py:MAX_CHAT_NAME_LENGTH
const MAX_CHAT_NAME_LENGTH = 255;

export function updateChatSessionInCache(
  apolloClient: ReturnType<typeof useApolloClient>,
  chatId: string,
  name: string,
  modifiedAt: string
): void {
  apolloClient.cache.modify({
    id: apolloClient.cache.identify({__typename: 'ChatSessionNode', id: chatId}),
    fields: {
      name: () => name,
      modifiedAt: () => modifiedAt,
    },
  });
}

export function RenameChatModal(props: {
  chatId: string;
  currentName: string;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(props.currentName);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const apolloClient = useApolloClient();
  const trimmedName = name.trim();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const {modifiedAt} = await updateChatSession(props.chatId, {name: trimmedName});
      updateChatSessionInCache(apolloClient, props.chatId, trimmedName, modifiedAt);
      props.onClose();
    } finally {
      setIsSaving(false);
    }
  }, [apolloClient, props, trimmedName]);

  return (
    <Modal
      title="Rename chat"
      onRequestClose={props.onClose}
      onRequestSubmit={handleSave}
      closeable={!isSaving}
      closeableOnBackgroundClick={!isSaving}
      medium={true}
      className={css(styles.renameChatModal)}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: isSaving ? 'Saving...' : 'Save',
          submit: true,
          className: css(styles.saveButton),
          disabled: !trimmedName || isSaving || trimmedName === props.currentName.trim(),
        },
      ]}
    >
      <TextInput
        size="md"
        aria-label="New chat name" /* Avoiding the label "name" to not trigger password manager autofill */
        ref={inputRef}
        maxLength={MAX_CHAT_NAME_LENGTH}
        value={name}
        disabled={isSaving}
        description={
          <>
            Current name: <em>{props.currentName}</em>
          </>
        }
        onChange={(e) => {
          setName(e.currentTarget.value);
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  renameChatModal: {
    display: 'flex',
    alignItems: 'center',
  },
  saveButton: {
    minWidth: 80,
  },
});
